<template>
  <div class="download-list-container">
    <el-popover
      placement="left-start"
      width="600"
      trigger="click"
      @show="loadDownloadTasks"
      popper-class="download-list-popover"
    >
      <template #default>
        <div class="download-list-content">
          <!-- 标题 -->
          <div class="header">
            <h3>下载任务中心</h3>
            <el-button 
              size="small" 
              @click="loadDownloadTasks"
              :loading="loading"
            >
              刷新
            </el-button>
          </div>

          <!-- 任务列表 -->
          <el-scrollbar max-height="25rem">
            <el-table 
              :data="taskList" 
              style="width: 100%"
              v-loading="loading"
              empty-text="暂无下载任务"
              :key="tableKey"
            >
              <el-table-column 
                prop="name" 
                label="任务名称" 
                width="250"
                show-overflow-tooltip
              />
              <el-table-column label="状态" width="100" >
                <template #default="{row}">
                  <el-tag :type="getStatusTagType(row.status)" size="small">
                    {{ getStatusText(row.status) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="进度" width="120" >
                <template #default="{row}">
                  <el-progress 
                    :percentage="row.progress || 0" 
                    :status="getProgressStatus(row.status)"
                    :stroke-width="16"
                    :text-inside="true"
                    style="width: 6.25rem"
                  />
                </template>
              </el-table-column>
              <el-table-column label="操作" width="90" fixed="right">
                <template #default="{row}">
                  <el-button 
                    size="small" 
                    type="primary" 
                    @click="handleDownload2(row)"
                    :disabled="row.status !== 2"
                    plain
                  >
                    下载
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
          </el-scrollbar>
        </div>
      </template>

      <!-- 触发图标 -->
      <template #reference>
        <el-badge 
          :value="activeTaskCount" 
          :max="99" 
          type="danger"
          class="badge-icon"
        >
          <svg-icon
            iconName="download-center"
            class="download-icon"
          />
        </el-badge>
      </template>
    </el-popover>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { message } from '@/utils/js/initVariable';
import { queryExportDataTaskProgress, download, downloadV2 } from '@/api/ExportData/index';
import { debounce } from 'lodash-es';
import axios from 'axios';
import store from '@/store';

const loading = ref(false);
const taskList = ref([]);
const pollTimer = ref(null);
const isMounted = ref(false);
const tableKey = ref(Date.now());

onMounted(() => {
  isMounted.value = true;
});

// 计算活跃任务数角标
const activeTaskCount = computed(() => {
  return taskList.value.filter(task => 
    task.status === 0 || task.status === 1 // 0=未开始,1=进行中
  ).length;
});

// 进度条状态
const getProgressStatus = (status) => {
  return status === 3 ? 'exception' : // 失败状态显示红色
         status === 2 ? 'success' :   // 完成状态显示绿色
         undefined;                  // 默认状态
};

// 图标颜色（根据主题变化）
const iconColor = computed(() => {
  return activeTaskCount.value > 0 
    ? 'var(--el-color-primary)' 
    : 'var(--el-text-color-secondary)';
});

// 加载下载任务列表
const loadDownloadTasks = async () => {
  if (!isMounted.value) return;
  
  try {
    loading.value = true;
    const res = await queryExportDataTaskProgress({});
    if (res.data.code === 1004) {
      taskList.value = res.data.dataList.map(item => ({
        ...item,
        progress: Math.min(100, Math.max(0, item.progress || 0)) // 确保进度在0-100之间
      }));
      tableKey.value = Date.now(); 
      checkPollingNeed();
    }
  } catch (err) {
    console.error('加载失败:', err);
    message('任务加载失败', 0);
  } finally {
    loading.value = false;
  }
};

const debouncedLoad = debounce(loadDownloadTasks, 300);

// 检查是否需要轮询
const checkPollingNeed = () => {
  // 检查是否有需要轮询的任务(状态为0或1)
  const needsPolling = taskList.value.some(
    task => task.status === 0 || task.status === 1
  );
  
  // 检查是否有失败任务(状态为3)
  const hasFailed = taskList.value.some(
    task => task.status === 3
  );
  
  if (needsPolling && !hasFailed && !pollTimer.value) {
    startPolling();
  } else if ((!needsPolling || hasFailed) && pollTimer.value) {
    stopPolling();
  }
};

// 开始轮询
const startPolling = () => {
  stopPolling();
  pollTimer.value = setInterval(() => {
    console.log( pollTimer.value);
    if (isMounted.value) {
      debouncedLoad();
    }
  }, 5000);
};

// 停止轮询
const stopPolling = () => {
  clearInterval(pollTimer.value);
  pollTimer.value = null;
};

const defaultRootURL = store.getters['defaultRootURL'];

// 下载文件
const handleDownload2 = async (task) => {
  console.log(task);
  const params = task.url;
  const url = defaultRootURL + downloadV2(params);
  window.open(url, '_blank');
}

// 下载文件
const handleDownload = async (task) => {
  try {
    // 确保传递正确的参数格式
    const params = task.taskId
    // console.log(params);
    const response = await download(params, {
      // responseType: 'blob'
    });
    const fileName = decodeURIComponent(response['headers']['content-disposition'].split("''").at(-1));
    let data = response.data, type;
    // if(fileName.includes('.geojson')){
    //   data = JSON.stringify(response.data);
    //   type = "application/json";
    // }
    // if(fileName.includes('.txt')){
    //   data = response.data;
    //   type = "text/plain";
    // }
    // if(fileName.includes('.zip')){
    //   data = response.data;
    //   type = "application/zip";
    // }
    
    const blob = new Blob([data]);
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 100);
    
    message('下载已开始', 1);
  } catch (err) {
    console.error('下载失败:', err);
    message('文件下载失败', 0);
  }
};

// 状态标签样式
const getStatusTagType = (status) => {
  const statusMap = {
    0: 'info',    // 未开始
    1: 'warning', // 进行中
    2: 'success', // 已完成
    3: 'danger'   // 失败
  };
  return statusMap[status] || 'info';
};

// 状态文本
const getStatusText = (status) => {
  const textMap = {
    0: '等待中',
    1: '处理中',
    2: '可下载',
    3: '失败'
  };
  return textMap[status] || '未知';
};

// 组件卸载时清理
onUnmounted(() => {
  stopPolling();
  isMounted.value = false;
});

// 初始加载
loadDownloadTasks();
</script>

<style lang="less" scoped>
.download-list-container {
  display: inline-block;
  margin-right: 15px;
  margin-bottom: 13px;
  .badge-icon {
    cursor: pointer;
    
    :deep(.el-badge__content) {
      top: 8px;
      right: 40px;
      
    }
  }
  
   .download-icon {
    width: 35px;
    height: 35px;
    color: white !important;  
    fill: currentColor;   
  }
}

.download-list-popover {
  padding: 12px !important;
  
  :deep(.el-table) {
    table {
      width: 100% !important;
    }
    
    .el-progress {
      width: 100%;
      min-width: 160px;
      
      &-bar {
        width: calc(100% - 6px);
      }
    }
    
    .el-table__cell {
      padding: 8px 0;
    }
  }
  
  .el-scrollbar__wrap {
    overflow-x: hidden;
  }
  
  .download-list-content {
    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
      
      h3 {
        margin: 0;
        font-size: 15px;
        color: var(--el-text-color-primary);
      }
    }
    
    .el-table {
      --el-table-border-color: var(--el-border-color-light);
      
      .el-button {
        padding: 5px 10px;
      }
      
      /* 进度条样式优化 */
      :deep(.el-progress) {
        .el-progress__text {
          font-size: 12px;
          color: var(--el-text-color-regular);
        }
      }
    }
  }
}
</style>